/******************************************************************************
 *** BgtOvrlGrnamtDialog ** 총괄 조합비 지출결의서 작성 * 교부금 등록
 *** - 사진 규칙 적용:
 ***   1) 교부금(지방본부비) = (총액 * 교부%)을 "천원단위 올림"
 ***   2) 운영금(본부비) = 총액 - 교부금 (차액으로 합 맞춤)
 ******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import Button from "@/js/common/ui/Button";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import TextBox from "@/js/common/ui/input/TextBox";

interface Props {
  ovrlMbsdsExpndDocId: string; // 지출결의서 문서 ID
  setGrnamtModalOpen: Function; // 교부금 모달 오픈
  setGrnamtSum: Function; // 교부금 합계 금액(= 조합비 합계 금액)
  setImplAmt: Function; // 집행금액(= 조합비 합계 금액)
}

const toNum = (v: any) => {
  const n = Number(Util.uncomma(String(v ?? "0")) || "0");
  return Number.isFinite(n) ? n : 0;
};

/** 천원단위 올림 */
const ceilToThousand = (n: number) => Math.ceil(n / 1000) * 1000;

/**
 * 로우 순서대로 운영% 고정
 */
const OPER_RT_BY_ROW_INDEX: number[] = [
  30, // 서울지방본부
  30, // 경인지방본부
  30, // 부산지방본부
  30, // 충청지방본부
  28, // 전남지방본부
  30, // 경북지방본부
  15, // 전북지방본부
  14, // 강원지방본부
  52, // 제주우체국
  48, // 서귀포우체국
  52, // 제주우편집중국
  20, // 우정인재개발원
  20, // 우정정보관리원
  20, // 우정사업조달센터
];

function calcRowByFixedOperRt(row: any, index: number) {
  const base = toNum(row.mbsdsSumAmt);

  const operRt = OPER_RT_BY_ROW_INDEX[index];
  const dtbnRt = 100 - operRt;

  // 1) 교부금 계산(천원단위 올림)
  const rawDtbn = (base * dtbnRt) / 100;
  let dtbnAmt = ceilToThousand(rawDtbn);

  // 총액 초과 방지(특히 base < 1000일 때)
  if (dtbnAmt > base) dtbnAmt = base;
  if (dtbnAmt < 0) dtbnAmt = 0;

  // 2) 운영금은 차액
  const operAmt = base - dtbnAmt;

  return {
    ...row,
    operRt: String(operRt),
    dtbnRt: String(dtbnRt),
    operAmt: String(operAmt),
    dtbnAmt: String(dtbnAmt),
  };
}

const BgtOvrlGrnamtDialog = ({ ovrlMbsdsExpndDocId, setGrnamtModalOpen, setGrnamtSum, setImplAmt }: Props) => {
  const [dataArr, setDataArr] = React.useState<any[]>([]);

  const [sumMbsdsSumAmt, setSumMbsdsSumAmt] = React.useState<string>("0");
  const [sumOperAmt, setSumOperAmt] = React.useState<string>("0");
  const [sumDtbnAmt, setSumDtbnAmt] = React.useState<string>("0");

  // 합계 재계산
  const recalcSum = (next: any[]) => {
    setSumMbsdsSumAmt(String(next.reduce((acc, r) => acc + toNum(r.mbsdsSumAmt), 0)));
    setSumOperAmt(String(next.reduce((acc, r) => acc + toNum(r.operAmt), 0)));
    setSumDtbnAmt(String(next.reduce((acc, r) => acc + toNum(r.dtbnAmt), 0)));
  };

  // 조회
  React.useEffect(() => {
    if (Util.isEmpty(ovrlMbsdsExpndDocId)) return;

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/bgt/ovrl/expnd/select-ovrl-grnamt-list.do",
      methodType: "post",
      paramType: "json",
      paramData: { ovrlMbsdsExpndDocId },
      onSuccessFn: (res: any) => {
        const list = res.result.list || [];

        // 화면은 "고정 운영%" + "교부금 천원단위 올림" 규칙으로 통일
        const normalized = list.map((r: any, idx: number) => {
          const mbsdsSumAmt = Number(r.mbsdsSumAmt) === 0 ? "" : (r.mbsdsSumAmt ?? "");
          return calcRowByFixedOperRt({ ...r, mbsdsSumAmt }, idx);
        });

        setDataArr(normalized);
        recalcSum(normalized);

        AppContext.hideSpinner();
      },
    });
  }, [ovrlMbsdsExpndDocId]);

  // =========================
  // 입력 핸들러 (입력은 "조합비총액" + "비고"만)
  // =========================
  const onChangeMbsdsSumAmt = (index: number, v: string) => {
    const next = [...dataArr];
    next[index] = calcRowByFixedOperRt({ ...next[index], mbsdsSumAmt: v }, index);
    setDataArr(next);
    recalcSum(next);
  };

  const onChangeRmrk = (index: number, v: string) => {
    const next = [...dataArr];
    next[index] = { ...next[index], rmrkCn: v };
    setDataArr(next);
  };

  // 저장
  function doSave() {
    if (Util.isEmpty(ovrlMbsdsExpndDocId)) return;
    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/bgt/ovrl/expnd/save-ovrl-grnamt.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        ovrlMbsdsExpndDocId,
        sumMbsdsSumAmt: sumMbsdsSumAmt,
        dataList: dataArr.map((r) => ({
          mbsdsGrnamtId: r.mbsdsGrnamtId,
          ovrlMbsdsExpndDocId,
          grnamtOrgCd: r.grnamtOrgCd,
          mbsdsSumAmt: r.mbsdsSumAmt === "" ? "0" : Util.uncomma(r.mbsdsSumAmt),
          operRt: Util.uncomma(String(r.operRt)),
          operAmt: Util.uncomma(String(r.operAmt)),
          dtbnRt: Util.uncomma(String(r.dtbnRt)),
          dtbnAmt: Util.uncomma(String(r.dtbnAmt)),
          rmrkCn: r.rmrkCn ?? "",
        })),
      },
      onSuccessFn: () => {
        AppContext.hideSpinner();
        setGrnamtModalOpen(false);
        setGrnamtSum(sumMbsdsSumAmt); // 교부금 합계(= 조합비 합계)
        setImplAmt(sumMbsdsSumAmt); // 집행금액(= 조합비 합계)
      },
    });
  }

  return (
    <div className="w100p dpFlx jcC">
      <div className="w900 p10">
        <div className="bd1 bdcEDEDED bdrd8 ofH">
          <table className="w100p tbl-collapse tlF">
            <colgroup>
              <col className="w120" /> {/* 지방본부별 */}
              <col className="w120" /> {/* 조합비총액 */}
              <col className="w60" /> {/* 운영 % */}
              <col className="w120" /> {/* 운영 금액 */}
              <col className="w60" /> {/* 교부 % */}
              <col className="w120" /> {/* 교부 금액 */}
              <col className="w150" /> {/* 비고 */}
            </colgroup>

            <thead>
              <tr className="h30">
                <th rowSpan={2} className="bd1 bdcEDEDED bgcF1F8FF taC fw700">
                  <span className="dpFlx aiC jcC fs12 fc2c3e50 wsNw">지방본부별</span>
                </th>
                <th rowSpan={2} className="bd1 bdcEDEDED bgcF1F8FF taC fw700">
                  <span className="dpFlx aiC jcC fs12 fc2c3e50 wsNw">조합비총액</span>
                </th>
                <th colSpan={2} className="bd1 bdcEDEDED bgcE8EEFF taC fw700">
                  <span className="dpFlx aiC jcC fs12 fc2c3e50 wsNw">운영금(본부비)</span>
                </th>
                <th colSpan={2} className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs12 fc2c3e50 wsNw">교부금(지방본부비)</span>
                </th>
                <th rowSpan={2} className="bd1 bdcEDEDED bgcF1F8FF taC fw700">
                  <span className="dpFlx aiC jcC fs12 fc2c3e50 wsNw">비고</span>
                </th>
              </tr>

              <tr className="h30">
                <th className="bd1 bdcEDEDED bgcE8EEFF taC fw700">
                  <span className="dpFlx aiC jcC fs11 fc777777 wsNw">%</span>
                </th>
                <th className="bd1 bdcEDEDED bgcE8EEFF taC fw700">
                  <span className="dpFlx aiC jcC fs11 fc777777 wsNw">금액</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs11 fc777777 wsNw">%</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs11 fc777777 wsNw">금액</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {dataArr.length === 0 ? (
                <tr>
                  <td colSpan={7} className="bd1 bdcEDEDED">
                    <span className="h44 dpFlx aiC jcC fc999999 fm-c fs13">조회된 데이터가 없습니다.</span>
                  </td>
                </tr>
              ) : (
                dataArr.map((row: any, index: number) => {
                  const zebraBg = index % 2 === 0 ? "bgcWhite" : "bgcF9F9F9";

                  return (
                    <tr key={index} className={zebraBg} style={{ height: "30px" }}>
                      {/* 지방본부 */}
                      <td className="bd1 bdcEDEDED">
                        <span className="dpFlx aiC jcC pw12 fm-c fs13 fc333333 wsNw ofH toE">{row.grnamtOrgNm}</span>
                      </td>

                      {/* 조합비총액(입력) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.mbsdsSumAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30 bd1 bdrd4"
                            inputClass="taR pr10"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            onChange={(data: string) => onChangeMbsdsSumAmt(index, data)}
                          />
                        </div>
                      </td>

                      {/* 운영% (고정/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcC pw8 fm-b fs13 fc333333">{String(row.operRt ?? "")}</div>
                      </td>

                      {/* 운영금액 (자동/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.operAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30 bd1 bdrd4"
                            inputClass="taR pr10"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            onChange={() => {}}
                            disabled={true}
                          />
                        </div>
                      </td>

                      {/* 교부% (고정/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcC pw8 fm-b fs13 fc333333">{String(row.dtbnRt ?? "")}</div>
                      </td>

                      {/* 교부금액 (자동/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.dtbnAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30 bd1 bdrd4"
                            inputClass="taR pr10"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            onChange={() => {}}
                            disabled={true}
                          />
                        </div>
                      </td>

                      {/* 비고(입력) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC pw12">
                          <TextBox
                            value={row.rmrkCn}
                            width={200}
                            height={30}
                            fontSize={13}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-0.6}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={4}
                            style={{ backgroundColor: "#fff", padding: "0 10px" }}
                            onChangeFunc={(text: string) => onChangeRmrk(index, text)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}

              {/* 합계 row */}
              <tr className="bgcF1F8FF h35">
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcC pw12 fm-b fs13 fc111111">합계</div>
                </td>
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcE pw12 fm-b fs13 fc111111">{Util.comma(sumMbsdsSumAmt)}</div>
                </td>
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcC pw8 fm-b fs12 fc666666">-</div>
                </td>
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcE pw12 fm-b fs13 fc111111">{Util.comma(sumOperAmt)}</div>
                </td>
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcC pw8 fm-b fs12 fc666666">-</div>
                </td>
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcE pw12 fm-b fs13 fc007bff">{Util.comma(sumDtbnAmt)}</div>
                </td>
                <td className="bd1 bdcEDEDED">
                  <div className="dpFlx aiC jcC pw12 fm-b fs12 fc666666"> </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 버튼 */}
        <div className="dpFlx aiC jcC gap10 mt20">
          <Button
            buttonName="취소"
            width={120}
            height={40}
            fontFamily="fm-b"
            fontSize={15}
            letterSpacing={-0.8}
            variant="styCancelBtn"
            borderRadius={6}
            buttonClass="dpFlx aiC jcC"
            onClickFunc={() => setGrnamtModalOpen(false)}
          />
          <Button
            width={120}
            height={40}
            fontFamily="fm-b"
            fontSize={15}
            letterSpacing={-0.8}
            variant="styCompleteBtn"
            borderRadius={6}
            buttonClass="dpFlx aiC jcC"
            buttonName="저장"
            onClickFunc={() => doSave()}
          />
        </div>
      </div>
    </div>
  );
};

export default BgtOvrlGrnamtDialog;
